<template>
  <div :class="{ 'has-error': !!errorMessage, success: meta.valid }" class="flex flex-col gap-1">
    <label class="font-semibold" :for="name">
      {{ label }}<span v-if="required" class="text-red-600">*</span>
    </label>

    <Multiselect
      ref="dropdown"
      v-model="inputValue"
      :options="combinedOptions"
      :multiple="false"
      :canClear="true"
      @change="emitValue"
    />

    <p v-show="errorMessage || meta.valid" class="help-message text-red-500">
      {{ errorMessage }}
    </p>
  </div>
</template>

<script>
import { toRef, ref } from 'vue';
import { useField } from 'vee-validate';
import { useI18n } from 'vue-i18n';
import * as yup from 'yup';

import Multiselect from '@vueform/multiselect';
import '@vueform/multiselect/themes/default.css';

import { appendSpinner, removeSpinner } from '@/helpers';
import { getSelectValuesCached } from '@/helpers/selectValuesCache'; // ✅ новый кэш

export default {
  components: { Multiselect },
  props: {
    type: { type: String, default: 'text' },
    value: { type: [String, Number], default: undefined },
    name: { type: String, required: true },
    label: { type: String, required: true },
    successMessage: { type: String, default: '' },
    placeholder: { type: String, default: '' },
    options: { type: Array, default: () => [] },
    required: { type: Boolean, default: true },
    property: { type: Number, default: undefined },
    defaultSelectValue: { type: Object, default: undefined },

    // если вдруг захочешь включить детей (редко нужно для SelectField)
    includeChildren: { type: Boolean, default: false },
    dataType: { type: Number, default: undefined },
  },

  setup(props) {
    const { t } = useI18n();
    const name = toRef(props, 'name');

    let rules = yup.number();
    if (props.required) {
      rules = rules.required(t('warning.REQUIRED_MESSAGE'));
    }

    const defaultValue = props?.defaultSelectValue?.id;

    const { meta, value, errorMessage, validate, handleBlur, handleChange } = useField(
      name,
      rules,
      { initialValue: defaultValue },
    );

    const inputValue = value;

    return {
      inputValue,
      errorMessage,
      validate,
      meta,
      handleBlur,
      handleChange,
    };
  },

  data() {
    return {
      currentOptions: [],
      isLoading: false,
    };
  },

  computed: {
    combinedOptions() {
      return (this.currentOptions || []).map(item => ({
        value: item.id,
        label: item.name,
      }));
    },
  },

  methods: {
    emitValue() {
      this.$emit('valueChanged', {
        value: this.inputValue,
        property: this.property,
        level: 1,
        data_type: this.dataType,
      });
    },

    async loadOptionsFromCache() {
      if (!this.property) return;

      this.isLoading = true;
      try {
        // ✅ берём из localStorage (или перекачиваем раз в 7 дней)
        const data = await getSelectValuesCached(this.property, {
          include_children: this.includeChildren,
        });

        // data = [{id,name,...}]
        this.currentOptions = Array.isArray(data) ? data : [];
      } catch (e) {
        console.error('SelectField cache load failed:', e);

        // fallback
        this.currentOptions = Array.isArray(this.options) ? this.options : [];
      } finally {
        this.isLoading = false;
      }
    },

    async validateField() {
      const validatedData = await this.validate();

      const v = this.inputValue;
      const normalized =
        v === null || v === undefined || v === '' || v === 'None' || v === 'null'
          ? []
          : [Number(v)];

      return {
        value: normalized.filter(x => Number.isInteger(x) && x > 0),
        valid: validatedData.valid,
        mode: 'multi',
        property: this.property,
      };
    },
  },

  watch: {
    inputValue() {
      this.emitValue();
    },

    isLoading(newVal) {
      const dropdown = this.$refs.dropdown;
      const list = dropdown?.$el?.querySelector('.multiselect-options');
      if (!list) return;

      if (newVal) appendSpinner(list);
      else removeSpinner(list);
    },
  },

  mounted() {
    this.loadOptionsFromCache();
  },
};
</script>

<style scoped>
/* Your styles here */
</style>
